import { transformPath } from "./App";
import { sanitizeProductName, getProductName } from "./utils";
import createAxios from "./services/httpCommon";

// Omnisend REST calls are proxied through our own backend, which adds the auth +
// Omnisend-Version headers and forwards to https://api.omnisend.com/api/*. The
// browser can't call Omnisend's v2026-03-15 API directly — its required
// Omnisend-Version header is rejected by Omnisend's CORS preflight. Reusing the
// app's createAxios keeps this on the exact same backend base URL as every other
// service call, so there is no separate URL to keep in sync here.
const OMNISEND_TRACK_ENDPOINT = "/omnisend/track";
const OMNISEND_IDENTIFY_ENDPOINT = "/omnisend/identify";
const BRAND_ID = process.env.REACT_APP_OMNISEND_BRAND_ID;

// Helper to extract email string from either a string or a user object
const getEmail = (param) => {
  if (typeof param === "string") return param;
  if (typeof param === "object" && param !== null) return param.email;
  return null;
};

/* -------------------- Initialize Snippet (For Page Views) -------------------- */
export const initOmnisendSnippet = () => {
  const envPrefix = process.env.REACT_APP_ENV_PREFIX || "production";

  if (!BRAND_ID) {
    console.warn("⚠️ OMNISEND BRAND_ID is missing! Page Views will fail.");
    return;
  }

  // Standard Snippet Initialization
  window.omnisend = window.omnisend || [];
  window.omnisend.push(["brandID", BRAND_ID]);

  // 1. ATTEMPT IDENTITY BEFORE FIRST PAGE VIEW
  let currentEmail = null;
  try {
    const rawUserData = localStorage.getItem(`${envPrefix}_userData`);
    if (rawUserData) {
      const userData = JSON.parse(rawUserData);
      if (userData?.email) {
        currentEmail = userData.email;

        window.omnisend.push([
          "identifyContact",
          {
            email: userData.email,
            firstName: userData.firstName || "",
            lastName: userData.lastName || "",
            phone: userData.phoneNumber || "",
          },
        ]);

        // Also trigger the API sync in the background
        identifyOmnisendUser(userData);
      }
    }
  } catch (err) {
    console.error("Error checking identity during init:", err);
  }

  // 2. NOW TRACK FIRST PAGE VIEW IS HANDLED BY APP.js (Avoid collisions)

  const existingScript = document.querySelector(
    `script[src*="omnisnippet1.com"]`,
  );
  if (!existingScript) {
    const script = document.createElement("script");
    script.async = true;
    script.src = `https://omnisnippet1.com/inshop/launcher-v2.js?brandID=${BRAND_ID}`;
    const firstScript = document.getElementsByTagName("script")[0];
    if (firstScript && firstScript.parentNode) {
      firstScript.parentNode.insertBefore(script, firstScript);
    } else {
      document.head.appendChild(script);
    }
  }
};

/* -------------------- Identify (Sync Contact) -------------------- */
export const identifyOmnisendUser = async (user, callback) => {
  if (!user?.email) {
    console.warn("❌ identifyOmnisendUser: Missing email.", user);
    if (callback) callback();
    return;
  }

  // 1. Identify in Client-side Snippet (Crucial for Page Views)
  if (window.omnisend) {
    window.omnisend.push([
      "identifyContact",
      {
        email: user.email,
        firstName: user.firstName,
        lastName: user.lastName,
        phone: user.phoneNumber || "",
      },
    ]);
  }

  // 2. Sync the contact server-side via the dedicated identify endpoint. This is
  //    fire-and-forget so it never blocks event tracking — the callback runs
  //    immediately below regardless of how this request resolves.
  const contactBody = {
    email: user.email,
    phone: user.phoneNumber || "",
    firstName: user.firstName || "",
    lastName: user.lastName || "",
  };

  createAxios()
    .then((api) => api.post(OMNISEND_IDENTIFY_ENDPOINT, contactBody))
    .catch((error) => {
      console.error(
        "❌ API Identify contact failed:",
        error.response?.data || error.message,
      );
    });

  if (callback) callback();
};

/* -------------------- Track Event (REST API) -------------------- */
export const trackOmnisend = async (eventName, payload) => {
  const email = getEmail(payload.email);
  if (!email) {
    console.warn(`OMNISEND trackOmnisend: No email found for "${eventName}"`);
    return;
  }

  const eventData = {
    eventName: eventName,
    email: email,
    payload: payload.properties || {},
  };

  // Only order-related events send eventVersion (the caller sets it explicitly)
  if (payload.eventVersion) {
    eventData.eventVersion = payload.eventVersion;
  }

  try {
    const api = await createAxios();
    await api.post(OMNISEND_TRACK_ENDPOINT, eventData);
  } catch (error) {
    console.error(
      `❌ API Track "${eventName}" Failed:`,
      error.response?.data || error.message,
    );
  }
};

/* -------------------- Helpers (Exports) -------------------- */

export const trackOmnisendPageViewed = (emailOrUser) => {
  const email = getEmail(emailOrUser);

  // 1. Ensure queue exists
  window.omnisend = window.omnisend || [];

  // 2. Identify for Live View Linkage
  if (email) {
    const identifyPayload = { email };
    if (typeof emailOrUser === "object" && emailOrUser !== null) {
      if (emailOrUser.firstName)
        identifyPayload.firstName = emailOrUser.firstName;
      if (emailOrUser.lastName) identifyPayload.lastName = emailOrUser.lastName;
      if (emailOrUser.phoneNumber)
        identifyPayload.phone = emailOrUser.phoneNumber;
    }
    window.omnisend.push(["identifyContact", identifyPayload]);
  }
};

export const trackOmnisendLoginSuccess = (user) => {
  const email = getEmail(user);
  if (!email) return;
  const payload = {
    email,
    properties: {
      login_at: new Date().toISOString(),
      status: "success",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
  };
  identifyOmnisendUser(user, () => trackOmnisend("successful login", payload));
};

export const trackOmnisendSignupFailure = (emailOrUser, firstName, reason) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  const payload = {
    email,
    properties: {
      failure_reason: String(reason),
      attempted_at: new Date().toISOString(),
      status: "failure",
      firstName: firstName || "",
      location_clicked: transformPath(window.location.pathname),
    },
  };
  identifyOmnisendUser({ email, firstName }, () => {
    trackOmnisend("failed signup", payload);
  });
};

export const trackOmnisendLoginFailure = (emailOrUser, reason) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  trackOmnisend("failed login", {
    email,
    properties: {
      failure_reason: String(reason),
      attempted_at: new Date().toISOString(),
      status: "failure",
    },
  });
};

export const trackOmnisendSignupSuccess = (user) => {
  const email = getEmail(user);
  if (!email) return;
  const payload = {
    email,
    properties: {
      signup_at: new Date().toISOString(),
      status: "success",
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
  };
  identifyOmnisendUser(user, () => trackOmnisend("successful signup", payload));
};

export const trackOmnisendLogout = (user) => {
  const email = getEmail(user);
  if (!email) return;
  trackOmnisend("logout", {
    email,
    properties: {
      logout_at: new Date().toISOString(),
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
    },
  });
};

export const trackOmnisendVirtuePlusRegistrationSuccess = (
  userOrEmail,
  firstName,
) => {
  const email = getEmail(userOrEmail);
  if (!email) return;
  const payload = {
    email,
    properties: {
      signup_at: new Date().toISOString(),
      status: "success",
      firstName: firstName || "",
      location_clicked: transformPath(window.location.pathname),
    },
  };
  identifyOmnisendUser({ email, firstName }, () =>
    trackOmnisend("successful virtue plus registration", payload),
  );
};

export const trackOmnisendVirtuePlusRegistrationFailure = (
  emailOrUser,
  firstName,
  reason,
) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  const payload = {
    email,
    properties: {
      failure_reason: String(reason),
      attempted_at: new Date().toISOString(),
      status: "failure",
      location_clicked: transformPath(window.location.pathname),
    },
  };

  identifyOmnisendUser({ email, firstName }, () => {
    trackOmnisend("failed virtue plus registration", payload);
  });
};

export const trackOmnisendProductViewed = (emailOrUser, product) => {
  const email = getEmail(emailOrUser);
  if (!product) return;

  const productProps = {
    productID: String(product.id),
    productTitle: product.title,
    productPrice: parseFloat(product.price) || 0,
    productCurrency: "NGN",
    productURL: product.url,
    productImageURL: product.image,
  };

  // 1. SNIPPET (For Live View & Sessions)
  if (window.omnisend) {
    if (email) {
      window.omnisend.push(["identifyContact", { email }]);
    }
    // $viewedProduct: reserved event that powers browse-abandonment + product recs.
    // Note: do NOT also push a plain "viewed product" here — the launcher treats
    // that name as a reserved standard event and throws (Cannot read 'id') when
    // the payload isn't in its expected catalog shape.
    window.omnisend.push(["track", "$viewedProduct", productProps]);
  }

  // 2. REST API (For Guaranteed History)
  if (email) {
    trackOmnisend("viewed product", { email, properties: productProps });
  }
};

export const trackOmnisendCategoryViewed = (emailOrUser, category) => {
  const email = getEmail(emailOrUser);
  if (!category || !email) return;
  const properties = {
    categoryID: String(category.id),
    categoryTitle: category.title,
    categoryURL: category.url,
    categoryImageURL: category.image,
  };
  trackOmnisend("viewed category", { email, properties });
};

export const trackOmnisendAddToCart = (emailOrUser, product, quantity) => {
  const email = getEmail(emailOrUser);
  if (!product) return;

  // 1. SNIPPET (For Live View & Sessions)
  if (window.omnisend) {
    if (email) {
      window.omnisend.push(["identifyContact", { email }]);
    }
    window.omnisend.push([
      "track",
      "added product to cart",
      {
        productID: String(product.id),
        productTitle: product.title,
        productPrice: parseFloat(product.price) || 0,
        productQuantity: parseInt(quantity) || 1,
        productCurrency: "NGN",
      },
    ]);
  }

  // 2. REST API (For Guaranteed History)
  if (email) {
    trackOmnisend("added product to cart", {
      email,
      properties: {
        productID: String(product.id),
        productTitle: product.title,
        productPrice: parseFloat(product.price) || 0,
        productQuantity: parseInt(quantity) || 1,
        productCurrency: "NGN",
      },
    });
  }
};

export const trackOmnisendCheckoutStarted = (emailOrUser, cart) => {
  const email = getEmail(emailOrUser);
  if (!cart) return;

  // 1. SNIPPET (For Live View & Sessions)
  if (window.omnisend) {
    if (email) {
      window.omnisend.push(["identifyContact", { email }]);
    }
    window.omnisend.push([
      "track",
      "started checkout",
      {
        cartID: "main_cart",
        abandonedCheckoutURL: window.location.origin + "/cart",
        totalPrice: parseFloat(cart.total) || 0,
        currency: "NGN",
        lineItems: cart.products.map((p) => ({
          productID: String(p.id),
          productTitle: p.title,
          productPrice: parseFloat(p.price) || 0,
          productQuantity: parseInt(p.quantity) || 1,
          productImageURL: p.image,
        })),
      },
    ]);
  }

  // 2. REST API (For Guaranteed History)
  if (email) {
    return trackOmnisend("started checkout", {
      email,
      properties: {
        cartID: "main_cart",
        abandonedCheckoutURL: window.location.origin + "/cart",
        totalPrice: parseFloat(cart.total) || 0,
        currency: "NGN",
        lineItems: cart.products.map((p) => ({
          productID: String(p.id),
          productTitle: p.title,
          productPrice: parseFloat(p.price) || 0,
          productQuantity: parseInt(p.quantity) || 1,
          productImageURL: p.image,
        })),
      },
    });
  }
};

export const trackOmnisendOrderPlaced = (
  emailOrUser,
  order,
  status,
  tags = [],
) => {
  const email = getEmail(emailOrUser);
  if (!email || !order) return;

  const getShoppingInstructions = (productName, shoppingInstructions) => {
    return shoppingInstructions
      ?.filter((instruction) =>
        instruction?.products?.some(
          (product) => product?.productName === productName,
        ),
      )
      .map((instruction) => instruction?.instruction?.name)
      .join(", ");
  };

  let items = [];
  if (order.cart && order.cart.length > 0) {
    items = order.cart;
  } else if (order.orderMod && order.orderMod.cart) {
    items = order.orderMod.cart;
  } else if (order.boxes && Object.keys(order.boxes).length > 0) {
    items = [order.boxes];
  } else if (
    order.purchasedVoucher &&
    Object.keys(order.purchasedVoucher).length > 0
  ) {
    items = [order.purchasedVoucher];
  }

  const lineItems = items.map((item) => {
    const productName =
      item.productId?.productName || item.box?.name || item.voucher?.name || "";
    const sku = item.productId?.sku || item.box?.sku || item.voucher?.sku || "";
    const instructions =
      item.boxShoppingInstructions?.length > 0
        ? item.boxShoppingInstructions.join(", ")
        : getShoppingInstructions(productName, order.shoppingInstructions);

    const productURL = item.productId?.productName
      ? `${window.location.origin}/products/${sanitizeProductName(getProductName(item.productId))}/${item.productId.sku}`
      : item.box?.name
        ? `${window.location.origin}/foodbundles`
        : item.voucher?.name
          ? `${window.location.origin}/vouchers`
          : window.location.origin;

    return {
      productID: String(
        item.productId?._id || item.box?.id || item.voucher?.id || "",
      ),
      productSKU: sku,
      productTitle: productName,
      productPrice: parseFloat(
        item.price ||
          item.productId?.price ||
          item.box?.amount ||
          item.amount ||
          0,
      ),
      productQuantity: parseInt(item.quantity) || 1,
      productImageURL: item.productId?.productImage || item.box?.boxImage || "",
      productURL: productURL,
      productDescription: item.productId?.description
        ? item.productId.description.replace(/<[^>]*>?/gm, "")
        : item.box?.description || "",
      productInstructions: instructions || "",
    };
  });

  const properties = {
    orderID: String(order.id),
    orderNumber: String(order.sku || order.id),
    totalPrice: parseFloat(order.totalCost || order.amount || 0),
    currency: "NGN",
    shippingPrice: parseFloat(order.fee || 0),
    shippingMethod: order.deliveryType || "",
    paymentMethod: order.paymentMethod || "",
    paymentStatus: order.paymentStatus || status || "placed",
    createdAt: order.createdAt || new Date().toISOString(),
    deliveryDate: order.deliveryDate?.split("T")[0] || "",
    note: order.orderComment || "",

    totalDiscount: parseFloat(
      (order.discountAmount || 0) + (order.referralDiscountAmount || 0),
    ),
    shippingAddress: order.address
      ? {
          address1: order.address.streetAddress || "",
          city: order.address.city?.name || "",
          state: order.address.state?.name || "",
          country: order.address.state?.country || "Nigeria",
          firstName: order.recipientName || order.user?.firstName || "",
          lastName: order.user?.lastName || "",
          phone: order.phoneNumber || order.user?.phoneNumber || "",
          landmark: order.address?.landmark || "",
        }
      : {},
    billingAddress: order.address
      ? {
          address1: order.address.streetAddress || "",
          city: order.address.city?.name || "",
          state: order.address.state?.name || "",
          country: order.address.state?.country || "Nigeria",
          firstName: order.recipientName || order.user?.firstName || "",
          lastName: order.user?.lastName || "",
          phone: order.phoneNumber || order.user?.phoneNumber || "",
        }
      : {},
    lineItems: lineItems,
    tags: tags,
    orderStatusURL: window.location.origin + "/profile/orders",
  };

  return trackOmnisend("placed order", {
    email,
    eventVersion: "v2",
    properties,
  });
};

export const trackOmnisendOrderPlacedFailed = (
  emailOrUser,
  reason,
  tags = [],
) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  trackOmnisend("failed order payment", {
    email,
    eventVersion: "v2",
    properties: {
      status: "failed",
      createdAt: new Date().toISOString(),
      tags: tags,
      orderUrl: window.location.href,
      failure_reason: String(reason),
    },
  });
};

export const trackOmnisendPaymentAttempt = (emailOrUser, order) => {
  const email = getEmail(emailOrUser);
  if (!email || !order) return;
  trackOmnisend("successful payment attempt", {
    email,
    properties: {
      totalPrice: parseFloat(order.total) || 0,
      currency: "NGN",
      lineItems: order.products.map((p) => ({
        productID: String(p.id),
        productTitle: p.title,
        productPrice: parseFloat(p.price) || 0,
        productQuantity: parseInt(p.quantity) || 1,
        productImageURL: p.image,
      })),
      discount: order.discount || 0,
      deliveryType: order.deliveryType || "",
    },
  });
};

export const trackOmnisendPaymentAttemptFailed = (emailOrUser, reason) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  trackOmnisend("failed payment attempt", {
    email,
    properties: {
      failure_reason: String(reason),
      attemptedAt: new Date().toISOString(),
    },
  });
};

export const trackOmnisendBoxesViewed = (emailOrUser, box) => {
  const email = getEmail(emailOrUser);
  if (!email || !box) return;
  const properties = {
    boxID: String(box.id),
    boxTitle: box.title,
    boxURL: box.url,
    boxImageURL: box.image,
  };
  trackOmnisend("viewed food bundle", { email, properties });
};

export const trackOmnisendAddedToWishlist = (emailOrUser, product) => {
  const email = getEmail(emailOrUser);
  if (!email || !product) return;
  const properties = {
    productID: String(product.id),
    productTitle: product.title,
    productPrice: parseFloat(product.price) || 0,
    productURL: product.url,
    productImageURL: product.image,
  };
  trackOmnisend("added product to wishlist", { email, properties });
};

export const trackOmnisendProductSearched = (emailOrUser, searchTerm) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  const properties = {
    search_term: searchTerm,
    searched_at: new Date().toISOString(),
    location_clicked: transformPath(window.location.pathname),
  };
  trackOmnisend("Product Searched", { email, properties });
};

export const trackOmnisendOrderReviewed = (emailOrUser) => {
  const email = getEmail(emailOrUser);
  if (email) trackOmnisend("Order Reviewed", { email });
};

export const trackOmnisendDeliveryDateSelected = (
  emailOrUser,
  deliveryDate,
  details,
) => {
  const email = getEmail(emailOrUser);
  if (!email) return;
  trackOmnisend("Delivery Date Selected", {
    email,
    properties: {
      date_selected: deliveryDate?.split("T")[0],
      state: details?.state || "",
      lga: details?.LGA || "",
    },
  });
};

export const trackOmnisendVoucherViewed = (emailOrUser, voucher) => {
  const email = getEmail(emailOrUser);
  if (!email || !voucher) return;
  const properties = {
    voucherID: String(voucher.id),
    voucherTitle: voucher.name,
    voucherDescription: voucher.description,
    voucherPrice: parseFloat(voucher.amount) || 0,
    voucherURL: window.location.origin + "/vouchers",
  };
  trackOmnisend("viewed voucher", { email, properties });
};
